import useLocalDataStore from "@/hooks/common/useLocalDataStore";
import {scrollRestore} from "@/hooks/common/useScrollRestoration";
import {departureStart, getTrafficIconUrl, returnArrival} from "@COMPONENTS/common/eventUtil";
import {FlagEmpasisCd, FlagEvProgDivi} from "@COMPONENTS/common/flag/Mobile";
import {EventExt, SortType} from "@COMPONENTS/product/common/popup/desktop/EventListPopup/EventListPopup";
import * as style from "@COMPONENTS/style/productlist/departurepopupM";
import AirlineIcon from "@FRAMEWORK/ui/mobile/icon/AirlineIcon";
import {Flex} from "@FRAMEWORK/ui/mobile/layout";
import {format} from "@yb/utils";
import {useRouter} from "next/router";
import React, {useMemo} from "react";

interface Props {
	eventList: EventExt[];
	sort: SortType;
	currentDate: string;
}

const EventList = ({eventList, sort, currentDate}: Props) => {
	const {getLocalData, setLocalData} = useLocalDataStore();
	const filters = getLocalData("departureFilters") ?? [];
	const router = useRouter();

	const sortedEventList = useMemo(() => {
		let filterList = [...eventList];
		const {prop, asc} = sort;

		if (currentDate) filterList = filterList.filter(v => v.outStartDt === currentDate);
		/** 정렬 */
		filterList = filterList.sort((a, b) => {
			if (asc) {
				// eslint-disable-next-line no-nested-ternary
				return Number(a[prop]) > Number(b[prop]) ? 1 : Number(a[prop]) < Number(b[prop]) ? -1 : 0;
			} else {
				// eslint-disable-next-line no-nested-ternary
				return Number(b[prop]) > Number(a[prop]) ? 1 : Number(b[prop]) < Number(a[prop]) ? -1 : 0;
			}
		});

		return filterList;
	}, [eventList, sort, currentDate]);

	// 예약 마감된 상품도 함께 보기
	const handleFilterReserve = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
		e.preventDefault();
		const copyArray = filters.filter(v => v.key !== "isEvReserve");
		setLocalData({key: "departureFilters", value: copyArray});
	};

	// 예약가능 여부
	const isReserved = filters.find(v => v.key === "isEvReserve")?.value === "true";

	/** 상품 클릭 위치 저장 */
	const {getRestoreData, setRestoreData} = scrollRestore();

	return (
		<>
			<style.ProductList>
				{sortedEventList.map((event, index) => (
					<style.ProductItem
						key={index}
						onClick={e => {
							e.preventDefault();
							/** 상품 클릭 데이터 저장 */
							setRestoreData({
								...getRestoreData(),
								goodsCd: event.evCd.split("-")[0],
								startMonth: event.outStartDt ?? "",
							});
							router.push(event.weblinkurl.replace(".yb", "") || "#");
						}}
					>
						<style.AirlineBox>
							<AirlineIcon
								img={getTrafficIconUrl(event)}
								title={event.trTp ? `${event.trCompanyNm}` : "교통불포함"}
								classes={event.seatClassCd && event.seatClassCd !== "EC" ? event.seatClassNm : ""}
							/>
							<Flex gap="3">
								<FlagEmpasisCd emphasisCd={event.emphasisCd} />
								<FlagEvProgDivi evProgDivi={event.evProgDivi} evProgDiviNm={event.evProgDiviNm} />
							</Flex>
						</style.AirlineBox>
						<style.Date>
							<span>{`${departureStart({
								evStartDt: event.outStartDt,
								outDeprtTm: event.outDeprtTm,
								isMobile: true,
							})} - ${returnArrival({
								evArriveDt: event.inArriveDt,
								inArrvTm: event.inArrvTm,
								isMobile: true,
							})}`}</span>
							<span>{event.dayStayInfo.replace(/^(\d)박(\d)일$/, "$1박 $2일")}</span>
						</style.Date>
						<style.ProductTitle>{event.evNm}</style.ProductTitle>
						{event.accomNm && (
							<style.PromotionOption>
								<span>옵션</span>
								{event.accomNm}
							</style.PromotionOption>
						)}
						<style.ProductPriceBox>
							<style.StatusText>
								{["F", "H"].indexOf(event.goodsTp) === -1 && event.evProgDivi !== "05" && (
									<span>(잔여 {event.avlilBlockQty}석)</span>
								)}
								{event.evReserveStatus === "end" && (
									<style.ReservationStatus state="deadline">예약마감</style.ReservationStatus>
								)}
								{event.evReserveStatus === "wait" && (
									<style.ReservationStatus state="waiting">예약대기</style.ReservationStatus>
								)}
								{event.evReserveStatus === "can" && (
									<style.ReservationStatus state="possible">
										{["F", "H"].indexOf(event.goodsTp) > -1 ? "예약접수가능" : "예약가능"}
									</style.ReservationStatus>
								)}
							</style.StatusText>
							<style.ProductPrice>
								<em>{format.koKRFormatNumber(event.sumPrice)}</em>원~
							</style.ProductPrice>
						</style.ProductPriceBox>
						{event.couponYn === "Y" && (
							<style.CouponBox>
								<em>쿠폰</em>
								<style.CouponIcon>
									<i>
										{event.couponOptTy === "M"
											? format.koKRFormatNumber(event.couponDiscAmt)
											: `${event.couponDiscRate}%`}
									</i>
								</style.CouponIcon>
							</style.CouponBox>
						)}
					</style.ProductItem>
				))}
			</style.ProductList>
			{!eventList ||
				(eventList.length === 0 && (
					<style.ProcutNone>
						<p>{isReserved ? "해당 조건으로 예약가능한 상품이 없습니다." : "해당 조건의 상품이 없습니다."}</p>
						{isReserved && (
							<a href="#" onClick={handleFilterReserve}>
								예약 마감된 상품도 함께 보기
							</a>
						)}
					</style.ProcutNone>
				))}
		</>
	);
};

export default EventList;
